import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Space, Table, Input, Tooltip, DatePicker, message, Card, Typography, Form, Popconfirm, Tag, Empty } from "antd";
import type { TableColumnsType } from "antd";
import { CarOutlined, PlusOutlined, SearchOutlined, ReloadOutlined, SaveOutlined, MailOutlined, PhoneOutlined } from "@ant-design/icons";
import "../style/Entetrprise.css";
import dayjs from "dayjs";
import { RootState } from "../redux/store/store";
import { getEnterpriseEnquiryRequest } from "../redux/actions/enterprisesEnquiryAction";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const PURPLE = "#89339c";

interface DataType {
    key: string;
    Enterprise_ID: string;
    Addeddate: string;
    CompanyName: string;
    Authorized_Person: string;
    Mobno: string;
    Address: string;
    email: string;
    Addedby: string;
    status?: string;
}

const pageWrapper: React.CSSProperties = {
    padding: "20px",
    background: "linear-gradient(135deg, #f3eef7 0%, #fbf8ff 100%)",
    boxSizing: "border-box",
};

const fitColumnStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
};

const EnterpriseEnquiry: React.FC = () => {
    const dispatch = useDispatch();
    const { list, loading, totalcount } = useSelector((state: RootState) => state.enterpriseEnquiry);

    const [searchText, setSearchText] = useState("");
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
        dayjs(),
        dayjs(),
    ]);

    const disableFutureDates = (current: dayjs.Dayjs) =>
        current.isAfter(dayjs(), "day");

    const [editingRecord, setEditingRecord] = useState<DataType | null>(null);
    const [form] = Form.useForm();

    const isEditing = Boolean(editingRecord);

    const dataSource: DataType[] = useMemo(
        () => (list || []).map((item: any) => ({ ...item, key: item.Enterprise_ID })),
        [list]
    );

    const fetchData = () => {
        dispatch(
            getEnterpriseEnquiryRequest({
                fromdate: dateRange[0].format("YYYY-MM-DD"),
                todate: dateRange[1].format("YYYY-MM-DD"),
            })
        );
    };

    const lastFetchedRangeRef = useRef<string | null>(null);
    useEffect(() => {
        if (!dateRange?.[0] || !dateRange?.[1]) return;
        const rangeKey = `${dateRange[0].valueOf()}|${dateRange[1].valueOf()}`;
        if (lastFetchedRangeRef.current === rangeKey) return;
        lastFetchedRangeRef.current = rangeKey;
        fetchData();
    }, [dateRange]);

    const resetFormToAddMode = () => {
        setEditingRecord(null);
        form.resetFields();
    };

    const handleEditRecord = (record: DataType) => {
        setEditingRecord(record);
        form.setFieldsValue({ ...record, date: dayjs(record.Addeddate) });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleDeleteRecord = (key: string) => {
        message.info("Delete endpoint not connected yet.");
    };

    const handleFormSubmit = (values: any) => {
        message.info("Add/update endpoint not connected yet.");
        resetFormToAddMode();
    };

    const clearFilters = () => {
        setSearchText("");
        setDateRange([dayjs(), dayjs()]);
    };

    const columns: TableColumnsType<DataType> = [
        {
            title: "Enquiry ID",
            dataIndex: "Enterprise_ID",
            key: "Enterprise_ID",
            align: "center",
            width: 140,
            fixed: "left",
            render: (text) => <span className="ent-id">{text}</span>,
        },
        {
            title: "Date",
            dataIndex: "Addeddate",
            key: "Addeddate",
            align: "center",
            width: 120,
            render: (val) => (
                <Text style={{ fontVariantNumeric: "tabular-nums" }}>{val ? dayjs(val).format("DD-MM-YYYY") : ""}</Text>
            ),
        },
        {
            title: "Company",
            dataIndex: "CompanyName",
            key: "CompanyName",
            align: "center",
            width: 230,
            render: (text, record) => (
                <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    <span className="ent-avatar">{(text || "?").charAt(0).toUpperCase()}</span>
                    <div style={{ lineHeight: 1.3, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, color: "#333" }}>{text}</div>
                        <div style={{ fontSize: 12, color: "#8c8c8c" }}>{record.Authorized_Person}</div>
                    </div>
                </div>
            ),
        },
        {
            title: "Contact",
            dataIndex: "Mobno",
            key: "Mobno",
            align: "center",
            width: 220,
            render: (text, record) => (
                <div style={{ lineHeight: 1.4, minWidth: 0 }}>
                    <a href={`tel:${text}`} className="ent-link">
                        <PhoneOutlined /> <span style={{ fontVariantNumeric: "tabular-nums" }}>{text}</span>
                    </a>
                    <br />
                    <a href={`mailto:${record.email}`} className="ent-link muted">
                        <MailOutlined /> {record.email}
                    </a>
                </div>
            ),
        },
        {
            title: "Address",
            dataIndex: "Address",
            key: "Address",
            align: "center",
            width: 260,
            render: (text) => (
                <Tooltip title={text?.length > 50 ? text : ""}>
                    <span className="ent-address">{text}</span>
                </Tooltip>
            ),
        },
    ];

    const filteredData = useMemo(() => {
        const text = searchText.trim().toLowerCase();
        if (!text) return dataSource;
        return dataSource.filter((item) =>
            item.Enterprise_ID?.toLowerCase().includes(text) ||
            item.CompanyName?.toLowerCase().includes(text) ||
            item.Authorized_Person?.toLowerCase().includes(text) ||
            item.Mobno?.includes(text) ||
            item.email?.toLowerCase().includes(text) ||
            item.Address?.toLowerCase().includes(text) ||
            item.Addedby?.toLowerCase().includes(text)
        );
    }, [dataSource, searchText]);

    const hasFilters = Boolean(searchText.trim());

    return (
        <div className="fit-page fit-page--split" style={pageWrapper}>
            <Card
                variant="borderless"
                styles={{ body: { padding: "12px 24px" } }}
                style={{
                    flex: "0 0 auto",
                    background: PURPLE,
                    borderRadius: 16,
                    boxShadow: "0 8px 24px rgba(137, 51, 156, 0.15)",
                    marginBottom: 12,
                    padding: "2px 8px",
                }}
            >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                    <div>
                        <span className="ent-eyebrow">Enquiries</span>
                        <Title level={2} style={{ color: "#fff", margin: "2px 0 0", fontWeight: 700, letterSpacing: "0.5px" }}>
                            Enterprise Enquiry Management
                        </Title>
                    </div>
                    <div className="ent-hero-pill">
                        <CarOutlined /> {totalcount} total
                    </div>
                </div>
            </Card>

            <div className="ent-layout">
                <Card
                    variant="borderless"
                    className={`ent-form-card fit-scroll-body ${isEditing ? "editing" : ""}`}
                    style={{
                        width: 360,
                        flexShrink: 0,
                        borderRadius: 16,
                        boxShadow: "0 6px 20px rgba(0,0,0,0.03)",
                        overflow: "hidden",
                        border: `2px solid ${isEditing ? "#d48806" : "#89339c86"}`,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            margin: "-24px -24px 20px",
                            padding: "16px 24px",
                            background: isEditing ? "#ad6800" : PURPLE,
                            borderRadius: "16px 16px 0 0",
                            transition: "background 0.2s",
                        }}
                    >
                        <Title level={4} style={{ margin: 0, color: "#fff", fontSize: 17 }}>
                            {isEditing ? "Edit enquiry" : "Add enquiry"}
                        </Title>
                        {isEditing && (
                            <Button
                                size="small"
                                icon={<PlusOutlined />}
                                onClick={resetFormToAddMode}
                                style={{ background: "#fff", color: "#ad6800", border: "none", fontWeight: 600 }}
                            >
                                New
                            </Button>
                        )}
                    </div>

                    {isEditing && (
                        <div className="ent-editing-note">
                            Editing <strong>{editingRecord?.Enterprise_ID}</strong>
                        </div>
                    )}

                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleFormSubmit}
                        requiredMark={false}
                        initialValues={{ Addedby: "Admin", date: dayjs() }}
                    >
                        <Form.Item name="date" label="Date" rules={[{ required: true, message: "Select a date" }]}>
                            <DatePicker
                                style={{ width: "100%" }}
                                format="DD-MM-YYYY"
                                allowClear={false}
                                disabledDate={disableFutureDates}
                            />
                        </Form.Item>

                        <Form.Item name="CompanyName" label="Company name" rules={[{ required: true, message: "Enter the company name" }]}>
                            <Input placeholder="Acme Corp" />
                        </Form.Item>

                        <Form.Item name="Authorized_Person" label="Authorized person" rules={[{ required: true, message: "Enter the contact person" }]}>
                            <Input placeholder="John Doe" />
                        </Form.Item>
                        <Form.Item
                            name="Mobno"
                            label="Mobile"
                            rules={[
                                { required: true, message: "Enter a mobile number" },
                                { pattern: /^\d{10}$/, message: "Must be 10 digits" },
                            ]}
                        >
                            <Input
                                prefix={<PhoneOutlined style={{ color: "#bbb" }} />}
                                placeholder="9876543210"
                                maxLength={10}
                                onKeyDown={(e) => {
                                    if (
                                        !/[0-9]/.test(e.key) &&
                                        !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)
                                    ) {
                                        e.preventDefault();
                                    }
                                }}
                                onChange={(e) => {
                                    e.target.value = e.target.value.replace(/\D/g, "");
                                }}
                            />
                        </Form.Item>

                        <Form.Item
                            name="email"
                            label="Email"
                            rules={[
                                { required: true, message: "Enter an email" },
                                { type: "email", message: "Enter a valid email" },
                            ]}
                        >
                            <Input prefix={<MailOutlined style={{ color: "#bbb" }} />} placeholder="contact@company.com" />
                        </Form.Item>

                        <Form.Item name="Address" label="Address" rules={[{ required: true, message: "Enter an address" }]}>
                            <Input.TextArea placeholder="Office / company address" rows={2} maxLength={200} showCount style={{ resize: "none" }} />
                        </Form.Item>

                        {/* <Form.Item name="Addedby" label={<span>Added by <em className="ent-opt">optional</em></span>}>
                            <Input placeholder="Admin" />
                        </Form.Item> */}

                        <Form.Item style={{ marginBottom: 0, marginTop: 14 }}>
                            <Space style={{ width: "100%", justifyContent: "center" }}>
                                {isEditing && <Button onClick={resetFormToAddMode}>Cancel</Button>}
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    icon={isEditing ? <SaveOutlined /> : <PlusOutlined />}
                                    style={{
                                        backgroundColor: isEditing ? "#ad6800" : PURPLE,
                                        borderColor: isEditing ? "#ad6800" : PURPLE,
                                        fontWeight: 600,
                                    }}
                                >
                                    {isEditing ? "Save changes" : "Add enquiry"}
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </Card>

                <div style={fitColumnStyle}>
                    <div className="toolbar-glass ent-toolbar">
                        <Space size={10} wrap>
                            <RangePicker
                                className="custom-range-picker"
                                format="DD-MM-YYYY"
                                value={dateRange}
                                disabledDate={disableFutureDates}
                                onChange={(dates) => dates && dates[0] && dates[1] && setDateRange(dates as any)}
                                style={{ height: 42, borderRadius: 8 }}
                                allowClear={false}
                                presets={[
                                    { label: "Today", value: [dayjs(), dayjs()] },
                                    { label: "Last 7 days", value: [dayjs().subtract(7, "day"), dayjs()] },
                                    { label: "Last 30 days", value: [dayjs().subtract(30, "day"), dayjs()] },
                                ]}
                            />
                            <Tooltip title="Refresh">
                                <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading} style={{ height: 42, borderRadius: 8 }} />
                            </Tooltip>
                            {hasFilters && (
                                <Tooltip title="Clear filters">
                                    <Button icon={<ReloadOutlined />} onClick={clearFilters} style={{ height: 42, borderRadius: 8 }} />
                                </Tooltip>
                            )}
                        </Space>

                        <Space size={12} wrap>
                            <div className="active-pill" style={{ fontSize: 15 }}>
                                <CarOutlined />
                                Total Enquiries : {filteredData.length}
                            </div>
                            <Input
                                allowClear
                                prefix={<SearchOutlined style={{ color: "#999" }} />}
                                placeholder="Search company, ID, person, email..."
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                style={{ width: 280, maxWidth: "100%", height: 42, borderRadius: 8 }}
                            />
                        </Space>
                    </div>

                    <div className="fit-table" style={{ background: "#fff", padding: 12, borderRadius: 16, boxShadow: "0 6px 20px rgba(0,0,0,0.03)" }}>
                        <Table<DataType>
                            bordered
                            className="enterprise-table"
                            columns={columns}
                            dataSource={filteredData}
                            rowKey="key"
                            loading={loading}
                            scroll={{ x: 1200, y: 240 }}
                            rowClassName={(record) => (record.key === editingRecord?.key ? "ent-row-editing" : "")}
                            locale={{
                                emptyText: (
                                    <Empty
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        description={
                                            <Text type="secondary">
                                                {hasFilters ? "No enquiries match your filters." : "No enquiries yet. Add one on the left."}
                                            </Text>
                                        }
                                    />
                                ),
                            }}
                            pagination={{
                                pageSize: 10,
                                pageSizeOptions: ["10", "25", "50", "100"],
                                showSizeChanger: true,
                                showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} enquiries`,
                            }}
                        />
                    </div>
                </div>
            </div>

            <style>
                {`
                .ent-layout {
                    display: flex;
                    gap: 20px;
                    flex: 1 1 auto;
                    min-height: 0;
                    /* stretch, not flex-start: the columns have to inherit the
                       row's height for the table to size against it. */
                    align-items: stretch;
                }
                @media (max-width: 1100px) {
                    .ent-layout { flex-direction: column; align-items: flex-start; }
                    .ent-layout > .ent-form-card { width: 100% !important; }
                }

                .ent-eyebrow {
                    display: block;
                    font-size: 11px;
                    font-weight: 600;
                    letter-spacing: 0.14em;
                    text-transform: uppercase;
                    color: rgba(255,255,255,0.7);
                }
                .ent-hero-pill {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    color: #fff;
                    font-weight: 600;
                    font-size: 14px;
                    background: rgba(255,255,255,0.16);
                    border: 1px solid rgba(255,255,255,0.28);
                    padding: 7px 16px;
                    border-radius: 999px;
                    font-variant-numeric: tabular-nums;
                }

                .ent-form-card { position: sticky; top: 16px; }
                @media (max-width: 1100px) { .ent-form-card { position: static; } }

                .ent-editing-note {
                    background: #fffbe6;
                    border: 1px solid #ffe58f;
                    color: #ad6800;
                    border-radius: 8px;
                    padding: 7px 12px;
                    font-size: 13px;
                    margin-bottom: 16px;
                }
                .ent-opt {
                    font-style: normal; font-weight: 400; font-size: 11px;
                    color: #aaa; margin-left: 4px;
                }

                .ent-toolbar {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 12px;
                }

                .enterprise-table .ant-table-thead > tr > th {
                    background: ${PURPLE} !important;
                    color: #fff !important;
                    text-align: center !important;
                    font-weight: 600 !important;
                    font-size: 12px !important;
                    letter-spacing: 0.05em;
                    text-transform: uppercase;
                }
                .enterprise-table .ant-table-thead > tr > th::before { display: none !important; }
                .enterprise-table .ant-table-column-sorter,
                .enterprise-table .ant-table-filter-trigger { color: rgba(255,255,255,0.6) !important; }
                .enterprise-table .ant-table-column-sorter-up.active,
                .enterprise-table .ant-table-column-sorter-down.active { color: #ffd666 !important; }
                .enterprise-table .ant-table-tbody > tr:nth-child(even) > td { background: #faf7fb; }
                .enterprise-table .ant-table-tbody > tr:hover > td { background: #f3e8f6 !important; }
                .enterprise-table .ant-table-tbody > tr.ent-row-editing > td {
                    background: #fffbe6 !important;
                    box-shadow: inset 3px 0 0 #d48806;
                }
                .enterprise-table .ant-table-cell-fix-left,
                .enterprise-table .ant-table-cell-fix-right { background: inherit !important; }

                .ent-id {
                    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
                    font-weight: 700;
                    font-size: 12.5px;
                    color: ${PURPLE};
                    background: #f6ecf8;
                    border: 1px solid #e8d5ed;
                    padding: 3px 8px;
                    border-radius: 6px;
                    white-space: nowrap;
                }
                .ent-avatar {
                    width: 32px; height: 32px; border-radius: 8px;
                    background: #f3e8f6; color: ${PURPLE};
                    display: inline-flex; align-items: center; justify-content: center;
                    font-weight: 700; font-size: 13px; flex-shrink: 0;
                }
                .ent-link {
                    color: #444; font-size: 13px; text-decoration: none;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .ent-link:hover { color: ${PURPLE}; }
                .ent-link.muted { color: #8c8c8c; font-size: 12.5px; }
                .ent-address {
                    color: #555; font-size: 13px;
                    display: -webkit-box; -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical; overflow: hidden;
                }
                `}
            </style>
        </div>
    );
};

export default EnterpriseEnquiry;